import { isEmpty } from 'lodash'
import ApiHelper from '../../helpers/ApiHelper'

export class ApiProduct {

    getBaseURL() {
        return ApiHelper.getBaseURL()
    }

    async signup(user) {
        return await ApiHelper.post(`/v1/user/signup`, user)
    }

    async verifyAccount(data) {
        return ApiHelper.post(`/v1/user/verify`, data)
    }

    async login(login) {
        return await ApiHelper.post(`/v1/user/login`, login)
    }

    async passwordRecover(data) {
        return await ApiHelper.post(`/v1/user/password-recover`, data)
    }

    async validatePasswordRecoverToken(token) {
        return await ApiHelper.get(`/v1/user/validate-password-recover-token/${token}`)
    }

    async confirmPasswordRecover(data) {
        return await ApiHelper.post(`/v1/user/confirm-password-recover`, data)
    }

    async updatePassword(data) {
        return await ApiHelper.put(`/v1/user/update-password`, data)
    }

    async getUsers() {
        return await ApiHelper.get(`/v1/user/get`)
    }

    async getUserById(userId) {
        return await ApiHelper.get(`/v1/user/get-by-id/${userId}`)
    }

    async createUser(user) {
        return await ApiHelper.post(`/v1/user/save`, user)
    }

    async updateUser(user) {
        return await ApiHelper.put(`/v1/user/update`, user)
    }

    async deleteUser(id) {
        return await ApiHelper.delete(`/v1/user/delete/${id}`)
    }

    async uploadUserAvatar(data) {
        return await ApiHelper.postWithFiles(`/v1/user/upload-avatar`, data)
    }

    async uploadCompanyLogo(data) { 
        return await ApiHelper.postWithFiles(`/v1/company/upload-logo`, data)
    }

    async updateCompany(data) { 
        return await ApiHelper.put(`/v1/company/update`, data)
    }

    // Dashboard
    async getDashboardMetrics(dateRange = 'week') {
        return await ApiHelper.get(`/v1/dashboard/get-metrics?dateRange=${dateRange}`)
    }

    // Companies
    async getMyCompanies() {
        return await ApiHelper.get(`/v1/company/my-companies`)
    }

    async getTeamMembers(companyId) {
        return await ApiHelper.get(`/v1/company/team-members/${companyId}`)
    }

    /// ROLES
    async getRoles() {
        return await ApiHelper.get(`/v1/role/get`)
    }

    async getRole(roleId) {
        return await ApiHelper.get(`/v1/role/get-by-id/${roleId}`)
    }

    async createRole(role) {
        return await ApiHelper.post(`/v1/role/save`, role)
    }

    async updateRole(role) {
        return await ApiHelper.put(`/v1/role/update`, role)
    }

    async deleteRole(id) {
        return await ApiHelper.delete(`/v1/role/delete/${id}`)
    }

    /// BRANDS
    async getBrands() {
        return await ApiHelper.get(`/v1/brand/get`)
    }

    async getBrandById(id) {
        return await ApiHelper.get(`/v1/brand/get-by-id/${id}`)
    }

    async createBrand(data) {
        return await ApiHelper.post(`/v1/brand/save`, data)
    }

    async updateBrand(data) {
        return await ApiHelper.put(`/v1/brand/update`, data)
    }

    async deleteBrand(id) {
        return await ApiHelper.delete(`/v1/brand/delete/${id}`)
    }

    async uploadBrandImage(data) {
        return await ApiHelper.postWithFiles(`/v1/brand/upload`, data)
    }

    /// BRANCHES
    async getBranches() {
        return await ApiHelper.get(`/v1/branch/get`)
    }

    async getBranchesByCustomerId(customerId) {
        return await ApiHelper.get(`/v1/branch/get-by-customer-id/${customerId}`)
    }

    async getBranch(id) {
        return await ApiHelper.get(`/v1/branch/get-by-id/${id}`)
    }

    async createBranch(data) {
        return await ApiHelper.post(`/v1/branch/save`, data)
    }

    async updateBranch(data) {
        return await ApiHelper.put(`/v1/branch/update`, data)
    }

    async deleteBranch(id) {
        return await ApiHelper.delete(`/v1/branch/delete/${id}`)
    }

    /// CUSTOMERS
    async getCustomerById(id) {
        return await ApiHelper.get(`/v1/user/customer/get-by-id/${id}`)
    }

    async getCustomers() {
        return await ApiHelper.get(`/v1/user/customer/get`)
    }

    async deleteCustomer(id) {
        return await ApiHelper.delete(`/v1/user/customer/delete/${id}`)
    }

    async createCustomer(data) {
        return await ApiHelper.post(`/v1/user/customer/save`, data)
    }

    async updateCustomer(data) {
        return await ApiHelper.put(`/v1/user/customer/update`, data)
    }

    // CUSTOMER BRANCHES
    async addCustomerBranches(data) {
        return await ApiHelper.post(`/v1/branch/save`, data)
    }

    async getBranchById(id) {
        return await ApiHelper.get(`/v1/branch/get-by-id/${id}`)
    }

    async getBranchSpend(branchId) {
        return await ApiHelper.get(`/v1/branch/get-spent/${branchId}`)
    }

    async getBranchBudgetLeft(branchId) {
        return await ApiHelper.get(`/v1/branch/get-budget-left/${branchId}`)
    }

    async deleteCustomerBranches(id) {
        return await ApiHelper.delete(`/v1/branch/delete/${id}`)
    }

    async updateCustomerBranches(data) {
        return await ApiHelper.put(`/v1/branch/update`, data)
    }

    /// PRODUCTS
    async getProducts(offset, limit, filter) {
        let moreParams = '&'
        if (!isEmpty(filter)) {
            for (const f of filter) {
                moreParams += `${f.columnName}=${f.value}`
            }
        }

        return await ApiHelper.get(`/v1/product/backoffice/get?offset=${offset}&limit=${limit}${moreParams}`)
    }

    async fetchDefaultPriceList() {
        return await ApiHelper.get('/v1/price-types/get-default')
    }

    async getPopularProducts() {
        return await ApiHelper.get(`/v1/product/backoffice/get-popular`)
    }

    async searchProducts(keyword, offset, limit) {
        return ApiHelper.get(`/v1/product/backoffice/search/${encodeURIComponent(keyword)}?offset=${offset}&limit=${limit}`)
    }

    async searchVariants(keyword = '') {
        return await ApiHelper.get(`/v1/product/backoffice/search-variants/${encodeURIComponent(keyword)}`)
    }

    async searchVariantsBySupplierId(keyword, supplierId) {
        return await ApiHelper.get(`/v1/product/search-variants-by-supplier/${keyword}/${supplierId}`)
    }

    async getProductById(id, includeVariants) {
        const query = includeVariants ? `?includeVariants=true` : ''
        return await ApiHelper.get(`/v1/product/backoffice/get-by-id/${id}${query}`)
    }

    async getProductPricingPreview(id) {
        return await ApiHelper.get(`/v1/pricing/preview/${id}`)
    }

    async createProduct(data) {
        return await ApiHelper.post(`/v1/product/backoffice/save`, data)
    }

    async updateProduct(data) {
        return await ApiHelper.post(`/v1/product/backoffice/update`, data)
    }

    async deleteProduct(id) {
        return await ApiHelper.delete(`/v1/product/backoffice/delete/${id}`)
    }

    async exportProducts(filters) {
        return await ApiHelper.post(`/v1/product/export`, filters)
    }

    async exportProductsForCostUpdate() {
        return await ApiHelper.download(`/v1/bulk-import/export-product-for-cost-update`, {}, 'POST')
    }

    async exportCatalogBulkTemplate() {
        return await ApiHelper.download(`/v1/bulk-import/catalog/template`)
    }

    async validateCatalogBulk(data) {
        return await ApiHelper.postWithFiles(`/v1/bulk-import/catalog/validate`, data)
    }

    async importCatalogBulk(data) {
        return await ApiHelper.postWithFiles(`/v1/bulk-import/catalog/import`, data)
    }

    async exportPricingShippingBulkTemplate(scope = 'storefront', materialCodes) {
        const filter = materialCodes === undefined ? '' : `&materialCodes=${encodeURIComponent(materialCodes.join(','))}`
        return await ApiHelper.download(`/v1/bulk-import/pricing-shipping/template?scope=${encodeURIComponent(scope)}${filter}`)
    }

    async validatePricingShippingBulk(data) {
        return await ApiHelper.postWithFiles(`/v1/bulk-import/pricing-shipping/validate`, data)
    }

    async importPricingShippingBulk(data) {
        return await ApiHelper.postWithFiles(`/v1/bulk-import/pricing-shipping/import`, data)
    }

    async exportOperationalCostsBulkTemplate() {
        return await ApiHelper.download(`/v1/bulk-import/operational-costs/template`)
    }

    async importOperationalCostsBulk(data) {
        return await ApiHelper.postWithFiles(`/v1/bulk-import/operational-costs/import`, data)
    }

    async exportProductsForStockUpdate(data) {
        return await ApiHelper.post(`/v1/product/export-product-for-stock-update`, data)
    }

    async uploadProductImage(data) {
        return await ApiHelper.postWithFiles(`/v1/product/backoffice/upload`, data)
    }

    async addProductVariant(data) {
        return await ApiHelper.post(`/v1/product/backoffice/add-variant`, data)
    }

    async deleteVariant(id) {
        return await ApiHelper.delete(`/v1/product/backoffice/delete-variant/${id}`)
    }

    async editVariant(data) {
        return await ApiHelper.put(`/v1/product/edit-variant`, data)
    }

    async deleteProductImage(productId, fileName) {
        return await ApiHelper.delete(`/v1/product/backoffice/images/delete/${productId}/${encodeURIComponent(fileName)}`)
    }

    async getProductSampleImages() {
        return await ApiHelper.get(`/v1/product-sample-images/backoffice`)
    }

    async uploadProductSampleImage(data) {
        return await ApiHelper.postWithFiles(`/v1/product-sample-images/backoffice/upload`, data)
    }

    async createProductSampleImage(data) {
        return await ApiHelper.post(`/v1/product-sample-images/backoffice/save`, data)
    }

    async updateProductSampleImage(data) {
        return await ApiHelper.put(`/v1/product-sample-images/backoffice/update`, data)
    }

    async deleteProductSampleImage(id) {
        return await ApiHelper.delete(`/v1/product-sample-images/backoffice/delete/${id}`)
    }

    async fetchMaterials() {
        return await ApiHelper.get(`/v1/materials/active`)
    }

    async getMaterials() { return await ApiHelper.get(`/v1/materials`) }
    async createMaterial(data) { return await ApiHelper.post(`/v1/materials`, data) }
    async updateMaterial(id, data) { return await ApiHelper.put(`/v1/materials/${id}`, data) }
    async archiveMaterial(id) { return await ApiHelper.delete(`/v1/materials/${id}`) }
    async getMaterialConfiguration(id) { return await ApiHelper.get(`/v1/materials/${id}/configuration`) }
    async updateMaterialPrices(id, prices) { return await ApiHelper.put(`/v1/materials/${id}/prices`, { prices }) }
    async updateMaterialShippingProfiles(id, shippingProfiles) { return await ApiHelper.put(`/v1/materials/${id}/shipping-profiles`, { shippingProfiles }) }
    async deactivateMaterialPrice(materialId, priceId) { return await ApiHelper.delete(`/v1/materials/${materialId}/prices/${priceId}`) }
    async deactivateMaterialShippingProfile(materialId, profileId) { return await ApiHelper.delete(`/v1/materials/${materialId}/shipping-profiles/${profileId}`) }
    async getHeroSlides() { return await ApiHelper.get(`/v1/hero-slides`) }
    async createHeroSlide(data) { return await ApiHelper.post(`/v1/hero-slides`, data) }
    async updateHeroSlide(id, data) { return await ApiHelper.put(`/v1/hero-slides/${id}`, data) }
    async deleteHeroSlide(id) { return await ApiHelper.delete(`/v1/hero-slides/${id}`) }
    async uploadHeroImage(data) { return await ApiHelper.postWithFiles(`/v1/hero-slides/upload`, data) }

    async getFrames() {
        return await ApiHelper.get(`/v1/frames`)
    }

    async uploadFrameImage(data) {
        return await ApiHelper.postWithFiles(`/v1/frames/upload`, data)
    }

    async createFrame(data) {
        return await ApiHelper.post(`/v1/frames`, data)
    }

    async updateFrame(id, data) {
        return await ApiHelper.put(`/v1/frames/${id}`, data)
    }

    async deleteFrame(id) {
        return await ApiHelper.delete(`/v1/frames/${id}`)
    }

    async getMarialuisas() {
        return await ApiHelper.get(`/v1/marialuisas`)
    }

    async createMarialuisa(data) {
        return await ApiHelper.post(`/v1/marialuisas`, data)
    }

    async updateMarialuisa(id, data) {
        return await ApiHelper.put(`/v1/marialuisas/${id}`, data)
    }

    async deleteMarialuisa(id) {
        return await ApiHelper.delete(`/v1/marialuisas/${id}`)
    }

    async setDefaultImage(data) {
        return await ApiHelper.put(`/v1/product/images/set-default`, data)
    }

    async getProductVariant(id) {
        return await ApiHelper.get(`/v1/product/backoffice/variants/get/${id}`)
    }

    async getProductGallery(productId) {
        return await ApiHelper.get(`/v1/product/gallery/get/${productId}`)
    }

    async getProductCodes() {
        return await ApiHelper.get(`/v1/product/get-product-codes`)
    }

    async pollBulkImport(identifier) {
        return await ApiHelper.get(`/v1/bulk-import/poll-import-process/${identifier}`)
    }

    /// CATEGORIES
    async getCategories(type) {
        const query = type ? `?type=${type}` : ''
        return await ApiHelper.get(`/v1/category/get${query}`)
    }

    async getCategoryById(id) {
        return await ApiHelper.get(`/v1/category/get-by-id/${id}`)
    }

    async getCategoriesByFamilyId(id) {
        return await ApiHelper.get(`/v1/category/get-by-family/${id}`)
    }

    async createCategory(data) {
        return await ApiHelper.post(`/v1/category/save`, data)
    }

    async updateCategory(data) {
        return await ApiHelper.put(`/v1/category/update`, data)
    }

    async deleteCategory(id) {
        return await ApiHelper.delete(`/v1/category/delete/${id}`)
    }

    async uploadCategoryImage(data) {
        return await ApiHelper.postWithFiles(`/v1/category/upload`, data)
    }

    /// FAMILIES
    async getFamilies() {
        return await ApiHelper.get(`/v1/family/get`)
    }

    async getFamilyById(id) {
        return await ApiHelper.get(`/v1/family/get-by-id/${id}`)
    }

    async createFamily(data) {
        return await ApiHelper.post(`/v1/family/save`, data)
    }

    async updateFamily(data) {
        return await ApiHelper.put(`/v1/family/update`, data)
    }

    async deleteFamily(id) {
        return await ApiHelper.delete(`/v1/family/delete/${id}`)
    }

    async uploadFamilyImage(data) {
        return await ApiHelper.postWithFiles(`/v1/family/upload`, data)
    }

    // SUBCATEGORIES
    async getSubcategories() {
        return await ApiHelper.get(`/v1/subcategory/get`)
    }

    async getSubcategoriesByCategoryId(id) {
        return await ApiHelper.get(`/v1/subcategory/get-by-category/${id}`)
    }

    async getSubcategoryById(id) {
        return await ApiHelper.get(`/v1/subcategory/get-by-id/${id}`)
    }

    async createSubcategory(data) {
        return await ApiHelper.post(`/v1/subcategory/save`, data)
    }

    async updateSubcategory(data) {
        return await ApiHelper.put(`/v1/subcategory/update`, data)
    }

    async deleteSubcategory(id) {
        return await ApiHelper.delete(`/v1/subcategory/delete/${id}`)
    }

    async uploadSubcategoryImage(data) {
        return await ApiHelper.postWithFiles(`/v1/subcategory/upload`, data)
    }

    /// DISCOUNTS AND COUPONS
    async getDiscounts() {
        return await ApiHelper.get(`/v1/backoffice/discounts/discounts`)
    }

    async createDiscount(data) {
        return await ApiHelper.post(`/v1/backoffice/discounts/discounts/save`, data)
    }

    async updateDiscount(data) {
        return await ApiHelper.put(`/v1/backoffice/discounts/discounts/update`, data)
    }

    async deleteDiscount(id) {
        return await ApiHelper.delete(`/v1/backoffice/discounts/discounts/delete/${id}`)
    }

    async getDiscountCodes() {
        return await ApiHelper.get(`/v1/backoffice/discounts/codes`)
    }

    async createDiscountCode(data) {
        return await ApiHelper.post(`/v1/backoffice/discounts/codes/save`, data)
    }

    async updateDiscountCode(data) {
        return await ApiHelper.put(`/v1/backoffice/discounts/codes/update`, data)
    }

    async deleteDiscountCode(id) {
        return await ApiHelper.delete(`/v1/backoffice/discounts/codes/delete/${id}`)
    }

    async getActiveMaterials() {
        return await ApiHelper.get(`/v1/materials/active`)
    }

    async getPersonalizationTemplates() {
        return await ApiHelper.get(`/v1/personalization-templates`)
    }

    async uploadPersonalizationAsset(data) {
        return await ApiHelper.postWithFiles(`/v1/personalization-templates/upload`, data)
    }

    async uploadPersonalizationMockup(data) {
        return await ApiHelper.postWithFiles(`/v1/personalization-templates/mockups/upload`, data)
    }

    async updatePersonalizationMockup(key, data) {
        return await ApiHelper.put(`/v1/personalization-templates/mockups/${key}`, data)
    }

    /// SUPPLIERS
    async getSuppliers() {
        return await ApiHelper.get(`/v1/supplier/get`)
    }

    async getSupplierById(id) {
        return await ApiHelper.get(`/v1/supplier/get-by-id/${id}`)
    }

    async createSupplier(data) {
        return await ApiHelper.post(`/v1/supplier/save`, data)
    }

    async updateSupplier(data) {
        return await ApiHelper.put(`/v1/supplier/update`, data)
    }

    async deleteSupplier(supplierId) {
        return await ApiHelper.delete(`/v1/supplier/delete/${supplierId}`)
    }

    async uploadSupplierImage(data) {
        return await ApiHelper.postWithFiles(`/v1/supplier/upload`, data)
    }

    // BANKS
    async getBankAccounts() {
        return await ApiHelper.get(`/v1/bank/get`)
    }

    async getBankAccount(id) {
        return await ApiHelper.get(`/v1/bank/account/${id}`)
    }

    async createBankAccount(data) {
        return await ApiHelper.post(`/v1/bank/save`, data)
    }

    async updateBankAccount(data) {
        return await ApiHelper.put(`/v1/bank/update`, data)
    }

    async deleteBankAccount(id) {
        return await ApiHelper.delete(`/v1/bank/delete/${id}`)
    }

    async incrementBalance(id, amount) {
        return await ApiHelper.put(`/v1/bank/increment-balance/${id}`, { amount })
    }

    async decrementBalance(id, amount) {
        return await ApiHelper.put(`/v1/bank/decrement-balance/${id}`, { amount })
    }

    async getCurrencies() {
        return await ApiHelper.get(`/v1/bank/get-currencies`)
    }

    // Expenses
    async getExpenses() {
        return await ApiHelper.get(`/v1/expenses/get`)
    }

    async getExpenseById(id) {
        return await ApiHelper.get(`/v1/expenses/get-by-id/${id}`)
    }

    async createExpense(data) {
        return await ApiHelper.post(`/v1/expenses/save`, data)
    }

    async updateExpense(data) {
        return await ApiHelper.put(`/v1/expenses/update`, data)
    }

    async uploadExpenseAttachment(data) {
        return await ApiHelper.postWithFiles(`/v1/expenses/upload`, data)
    }

    async getExpenseTypes() {
        return await ApiHelper.get(`/v1/expenses/get-types`)
    }

    async deleteExpense(id) {
        return await ApiHelper.delete(`/v1/expenses/delete/${id}`)
    }

    // Settings
    async getSettingsByCode(code) {
        return await ApiHelper.get(`/v1/settings/get/${code}`)
    }

    async getSettings() {
        return await ApiHelper.get(`/v1/settings/get-all`)
    }

    // Sales
    async getSaleById(saleId) {
        return await ApiHelper.get(`/v1/sales/get-by-id/${saleId}`)
    }

    async saveSale(sale) {
        return await ApiHelper.post(`/v1/sales/save`, sale)
    }

    async fetchLastFolio() {
        return await ApiHelper.get(`/v1/sales/get-last-folio`)
    }

    async fetchNextFolio() {
        return await ApiHelper.get(`/v1/sales/get-next-folio`)
    }

    async getSales() {
        return await ApiHelper.get(`/v1/sales/get`)
    }

    async exportSalesOrders(params) {
        return ApiHelper.download('/v1/sales/export', params, 'POST')
    }

    async exportOrder(orderId) {
        return ApiHelper.download(`/v1/sales/export/${orderId}`)
    }

    ////// stock control
    async getVariantsWithLowStock() {
        return await ApiHelper.get(`/v1/stock/get-low-stock-variants`)
    }

    async getProductsBySupplierId(supplierId) {
        return await ApiHelper.get(`/v1/product/get-by-supplier/${supplierId}`)
    }

    // purchase orders
    async getNextPurchaseOrderFolio() {
        return await ApiHelper.get(`/v1/purchase-order/get-next-folio`)
    }

    async savePurchaseOrder(data) {
        return await ApiHelper.post(`/v1/purchase-order/save`, data)
    }

    async getPurchaseOrders() {
        return await ApiHelper.get(`/v1/purchase-order/get`)
    }

    async getPurchaseOrderById(id) {
        return await ApiHelper.get(`/v1/purchase-order/get-by-id/${id}`)
    }

    async getPurchaseOrderForPrintById(id) {
        return await ApiHelper.get(`/v1/purchase-order/get-for-print/${id}`)
    }

    async postBulkImportProduct(data) {
        return await ApiHelper.postWithFiles(`/v1/bulk-import/products`, data)
    }

    async postBulkImportCustomers(data) {
        return await ApiHelper.postWithFiles(`/v1/bulk-import/customers`, data)
    }

    async postBulkImportBranches(data) {
        return await ApiHelper.postWithFiles(`/v1/bulk-import/branches`, data)
    }

    async postBulkImportCosts(data) {
        return await ApiHelper.postWithFiles(`/v1/bulk-import/costs`, data)
    }

    async cancelPurchaseOrder(id) {
        return await ApiHelper.delete(`/v1/purchase-order/cancel/${id}`)
    }

    async markOrderAsPaid(order) {
        return await ApiHelper.post(`/v1/purchase-order/mark-as-paid`, { orderData: order})
    }

    async receiveProducts(orderId, lineItems) {
        return await ApiHelper.post(`/v1/purchase-order/receive-products/${orderId}`, { lineItems })
    }

    // purchase quotes
    async getNextPurchaseQuoteFolio() {
        return await ApiHelper.get(`/v1/purchase-quote/get-next-folio`)
    }

    async savePurchaseQuote(data) {
        return await ApiHelper.post(`/v1/purchase-quote/save`, data)
    }

    async savePurchaseQuoteAsDraft(data) {
        return ApiHelper.post(`/v1/purchase-quote/save-draft`, data)
    }

    async getPurchaseQuote() {
        return await ApiHelper.get(`/v1/purchase-quote/get`)
    }

    async getPurchaseQuoteById(id) {
        return await ApiHelper.get(`/v1/purchase-quote/get-by-id/${id}`)
    }

    async getPurchaseQuoteForPrintById(id) {
        return await ApiHelper.get(`/v1/purchase-quote/get-for-print/${id}`)
    }

    async cancelPurchaseQuote(id) {
        return await ApiHelper.delete(`/v1/purchase-quote/cancel/${id}`)
    }

    async markPurchaseQuoteAsOrder(order) {
        return await ApiHelper.post(`/v1/purchase-quote/mark-as-ordered`, order)
    }

    // Sales Order
    async getNextSalesOrderFolio() {
        return await ApiHelper.get(`/v1/sales/get-next-folio`)
    }

    async saveSalesOrder(data) {
        return await ApiHelper.post(`/v1/sales/save`, data)
    }

    async getSalesOrders() {
        return await ApiHelper.get(`/v1/sales/get`)
    }

    async getSalesOrderById(id) {
        return await ApiHelper.get(`/v1/sales/get-by-id/${id}`)
    }

    async getSalesOrderForPrintById(id) {
        return await ApiHelper.get(`/v1/sales/get-for-print/${id}`)
    }

    async cancelSalesOrder(id) {
        return await ApiHelper.delete(`/v1/sales/cancel/${id}`)
    }

    async confirmSalesOrder(saleId, shipment = {}) {
        return await ApiHelper.post(`/v1/sales/confirm`, { saleId, ...shipment })
    }

    async saveActualShippingCost(saleId, amount) {
        return await ApiHelper.post('/v1/sales/actual-shipping-cost', { saleId, amount })
    }

    async reconcileShippingCost(saleId) {
        return await ApiHelper.post('/v1/sales/reconcile-shipping-cost', { saleId })
    }

    async updateSalesOrderTracking(data) {
        return await ApiHelper.post(`/v1/sales/update-tracking`, data)
    }

    async markSalesOrderAsDelivered(saleId) {
        return await ApiHelper.post(`/v1/sales/mark-as-delivered`, { saleId })
    }

    async markSalesOrderAsShipped(data) {
        return await ApiHelper.post(`/v1/sales/mark-as-shipped`, data)
    }

    async markSalesOrderAsPaid(data) {
        return await ApiHelper.post(`/v1/sales/mark-as-paid`, data)
    }

    async saleOrderFulfillProducts(orderId, lineItems) {
        return await ApiHelper.post(`/v1/sales/fulfill/${orderId}`, { lineItems })
    }

    async getCountries() {
        return await ApiHelper.get(`/v1/country/get`)
    }

    async getStates(countryId) {
        return await ApiHelper.get(`/v1/state/get-by-countryId/${countryId}`)
    }
}

const apiProduct = new ApiProduct()

export default apiProduct
